import { useState } from 'react';
import { IconCreditCard, IconSend, IconLoader2 } from '@tabler/icons-react';
import api from '../lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import PageHeader from '../components/PageHeader';
import MainCard from '../components/MainCard';

interface PaymentResponse {
  status_code: number;
  status_message: string;
  title: string;
  message: string;
  payment_response?: {
    status_code: number;
    status_message: string;
    transaction_status_code: string;
    transaction_status_description: string;
    result_code: string;
    result_description: string;
    pay_request_id: string;
    transaction_id: string;
    voucher_type: string;
    voucher_amount: number;
  };
  account_response?: {
    status_code: number;
    data: {
      message: string;
      balances: any;
    };
  };
}

export function OttVoucherPage() {
  const [voucherCode, setVoucherCode] = useState('');
  const [msisdn, setMsisdn] = useState('');
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [response, setResponse] = useState<PaymentResponse | null>(null);

  const showAmountField = voucherCode === '112233445566';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setResponse(null);
    setLoading(true);

    try {
      if (!voucherCode.trim()) {
        throw new Error('Voucher Code is required');
      }

      if (!msisdn.trim()) {
        throw new Error('MSISDN is required');
      }

      if (showAmountField) {
        const amountNum = parseFloat(amount);
        if (isNaN(amountNum) || amountNum < 0 || amountNum > 99) {
          throw new Error('Amount must be a number between 0 and 99');
        }
      }

      const requestBody: any = {
        voucherCode: voucherCode.trim(),
        msisdn: msisdn.trim(),
      };

      if (showAmountField) {
        requestBody.amount = parseFloat(amount);
      }

      const response = await api.post('/vouchers/ott-voucher', requestBody);

      if (response.data.success && response.data.data) {
        const paymentData = response.data.data;
        setResponse(paymentData);
        
        if (paymentData.status_code && paymentData.status_code !== 4001) {
          setError(paymentData.message || paymentData.status_message || 'Payment failed');
        } else {
          setSuccess(paymentData.message || 'Payment processed successfully');
        }
      } else {
        throw new Error(response.data.message || 'Payment request failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to process payment');
      console.error('Payment error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="OTT Voucher"
        subtitle="Process OTT voucher payments"
      />

      <MainCard className="mb-6">
        <form onSubmit={handleSubmit}>
          <div className="flex flex-col gap-6">
            <div className="space-y-2">
              <Label htmlFor="voucherCode">Voucher Code</Label>
              <Input
                id="voucherCode"
                value={voucherCode}
                onChange={(e) => {
                  setVoucherCode(e.target.value);
                  if (e.target.value !== '112233445566') {
                    setAmount('');
                  }
                }}
                placeholder="Enter voucher code"
                required
              />
              <p className="text-xs text-muted-foreground">
                {showAmountField ? 'Special voucher code detected - amount field will appear' : 'Enter voucher code'}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="msisdn">MSISDN</Label>
              <Input
                id="msisdn"
                value={msisdn}
                onChange={(e) => setMsisdn(e.target.value)}
                placeholder="27716054142"
                required
              />
              <p className="text-xs text-muted-foreground">Enter customer MSISDN</p>
            </div>

            {showAmountField && (
              <div className="space-y-2">
                <Label htmlFor="amount">Amount</Label>
                <Input
                  id="amount"
                  type="number"
                  value={amount}
                  onChange={(e) => {
                    const value = e.target.value;
                    const numValue = parseFloat(value);
                    if (value === '' || (!isNaN(numValue) && numValue >= 0 && numValue <= 99)) {
                      setAmount(value);
                    }
                  }}
                  placeholder="0-99"
                  required
                  min={0}
                  max={99}
                  step={0.01}
                />
                <p className="text-xs text-muted-foreground">Enter amount between 0 and 99</p>
              </div>
            )}

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            {success && (
              <Alert>
                <AlertDescription className="text-emerald-700 dark:text-emerald-400">{success}</AlertDescription>
              </Alert>
            )}

            <Button
              type="submit"
              size="lg"
              disabled={loading}
              className="w-full"
            >
              {loading ? <IconLoader2 className="size-5 animate-spin" /> : <IconSend className="size-5" />}
              {loading ? 'Processing...' : 'Process Payment'}
            </Button>
          </div>
        </form>
      </MainCard>

      {response && (
        <MainCard>
          <h3 className="text-lg font-semibold mb-4">Payment Response</h3>
          <div className="space-y-2">
            <p className="text-sm">
              <strong>Status:</strong> {response.status_code} - {response.status_message}
            </p>
            <p className="text-sm">
              <strong>Title:</strong> {response.title}
            </p>
            <p className="text-sm mb-4">
              <strong>Message:</strong> {response.message}
            </p>

            {response.payment_response && (
              <div className="rounded-lg bg-muted/50 p-4 space-y-1">
                <p className="text-sm font-medium mb-2">Payment Response:</p>
                <p className="text-sm">
                  <strong>Status:</strong> {response.payment_response.status_code} - {response.payment_response.status_message}
                </p>
                <p className="text-sm">
                  <strong>Transaction ID:</strong> {response.payment_response.transaction_id || 'N/A'}
                </p>
                <p className="text-sm">
                  <strong>Pay Request ID:</strong> {response.payment_response.pay_request_id}
                </p>
                <p className="text-sm">
                  <strong>Voucher Type:</strong> {response.payment_response.voucher_type}
                </p>
                <p className="text-sm">
                  <strong>Voucher Amount:</strong> {response.payment_response.voucher_amount}
                </p>
              </div>
            )}

            {response.account_response && response.account_response.data && (
              <div className="rounded-lg bg-muted/50 p-4 space-y-1">
                <p className="text-sm font-medium mb-2">Account Response:</p>
                <p className="text-sm">
                  <strong>Status:</strong> {response.account_response.status_code}
                </p>
                {response.account_response.data.message && (
                  <p className="text-sm">
                    <strong>Message:</strong> {response.account_response.data.message}
                  </p>
                )}
              </div>
            )}
          </div>
        </MainCard>
      )}
    </div>
  );
}
